import React from 'react';
import { X, Check } from 'lucide-react';
import { type Product } from '../../services/products.service';
import { formatSAR } from '../../utils/formatters';

interface LearnMoreModalProps {
  isOpen: boolean;
  product: Product;
  onClose: () => void;
  onSelect?: () => void;
}

const categoryLabels: Record<string, string> = {
  Mobility: 'Mobility',
  '5G': '5G Fixed',
  Fixed_BBI: 'Fixed Broadband',
  Fixed_BDI: 'Fixed Dedicated',
};

const categoryColors: Record<string, { bg: string; text: string }> = {
  '5G':       { bg: '#EEF2FF', text: '#4F46E5' },
  Fixed_BBI:  { bg: '#E0F7FA', text: '#0097A7' },
  Fixed_BDI:  { bg: '#FFF3E0', text: '#E65100' },
  Mobility:   { bg: '#F3E8FF', text: '#7E22CE' },
};

export const LearnMoreModal: React.FC<LearnMoreModalProps> = ({ isOpen, product, onClose, onSelect }) => {
  if (!isOpen) return null;

  const features = product.description
    ? product.description.split(/[;\n]/).map((f) => f.trim()).filter(Boolean)
    : [];

  const price = Number(product.price) || 0;
  const vatRate = Number(product.vatRate) || 0.15;
  const priceExclVat = price / (1 + vatRate);
  const vatAmount = price - priceExclVat;
  const catColor = categoryColors[product.category] ?? { bg: '#F3F4F6', text: '#374151' };

  // Extract contract months from contractTerms string (e.g. "12-month minimum contract...")
  const contractMonths = product.contractTerms
    ? (product.contractTerms.match(/(\d+)-month/i)?.[1] ?? null)
    : null;

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', backdropFilter: 'blur(2px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: 'var(--surface)', borderRadius: 16, width: '100%', maxWidth: 560, boxShadow: 'var(--shadow-xl)', overflow: 'hidden', animation: 'modalIn .2s ease' }}
      >
        <div style={{ padding: '24px 24px 0' }}>
          {/* Header row */}
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
            <div>
              <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: catColor.bg, color: catColor.text, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8 }}>
                {categoryLabels[product.category] ?? product.category}
              </span>
              <h2 style={{ fontSize: 20, fontWeight: 800, color: 'var(--text-1)', margin: '0 0 4px' }}>{product.name}</h2>
              {features[0] && <p style={{ fontSize: 13.5, color: 'var(--text-2)', margin: 0 }}>{features[0]}</p>}
            </div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-2)', display: 'flex', padding: 4, borderRadius: 6, flexShrink: 0 }}>
              <X size={18} />
            </button>
          </div>

          {/* Pricing box */}
          <div style={{ background: 'var(--p-pale)', border: '1px solid var(--p-light)', borderRadius: 10, padding: '16px 20px', marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>Monthly Price</div>
              <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--text-1)', lineHeight: 1 }}>
                SAR {new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(priceExclVat)}
              </div>
              <div style={{ fontSize: 12.5, color: 'var(--text-2)', marginTop: 3 }}>
                + VAT SAR {vatAmount.toFixed(2)} = SAR {price.toFixed(2)} / month
              </div>
              <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--p)', background: 'var(--p-light)', padding: '2px 8px', borderRadius: 20 }}>Monthly billing</span>
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>Contract</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-1)' }}>{contractMonths ?? 12} months</div>
              <div style={{ fontSize: 12, color: 'var(--text-3)' }}>Minimum term</div>
            </div>
          </div>
        </div>

        <div style={{ padding: '0 24px 24px', overflowY: 'auto', maxHeight: 'calc(80vh - 280px)' }}>
          {/* What's included */}
          {features.length > 1 && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-1)', marginBottom: 10 }}>What's included</div>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {features.slice(1).map((feat, i) => (
                  <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13.5, color: 'var(--text-1)' }}>
                    <Check size={14} color="var(--succ)" style={{ flexShrink: 0, marginTop: 2 }} />
                    {feat}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Terms & Conditions */}
          {product.contractTerms && (
            <div style={{ background: 'var(--surface-alt)', borderRadius: 8, padding: '12px 14px', marginBottom: 20 }}>
              <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>Terms &amp; Conditions</div>
              <div style={{ fontSize: 12.5, color: 'var(--text-2)', lineHeight: 1.6 }}>{product.contractTerms}</div>
            </div>
          )}

          {/* Fees */}
          {(product.setupFee != null || product.cancellationFee != null || product.installationTimeline) && (
            <div style={{ background: 'var(--surface-alt)', borderRadius: 8, padding: '12px 14px', marginBottom: 20, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {product.setupFee != null && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--text-2)' }}>
                  <span>Setup Fee</span><strong style={{ color: 'var(--text-1)' }}>{formatSAR(product.setupFee)}</strong>
                </div>
              )}
              {product.cancellationFee != null && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--text-2)' }}>
                  <span>Cancellation Fee</span><strong style={{ color: 'var(--text-1)' }}>{formatSAR(product.cancellationFee)}</strong>
                </div>
              )}
              {product.installationTimeline && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--text-2)' }}>
                  <span>Installation</span><strong style={{ color: 'var(--text-1)' }}>{product.installationTimeline}</strong>
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={onClose} style={{ flex: 1, padding: '11px 16px', background: 'transparent', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: 13.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', color: 'var(--text-2)' }}>
              Close
            </button>
            {onSelect && (
              <button onClick={() => { onClose(); onSelect(); }} style={{ flex: 2, padding: '11px 16px', background: 'var(--p)', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7 }}>
                <Check size={15} />
                Select This Plan
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
